import { inject, Injectable } from '@angular/core'
import {
  GuardsCheckEnd,
  NavigationCancel,
  NavigationEnd,
  NavigationError,
  Router
} from '@angular/router'
import { SpinnerService } from './spinner.service'

/**
 * Shows the global spinner while a route change is in flight.
 *
 * OrderCacheResolverV2's cache API call happens on route change, so without a spinner the user briefly sees the
 * previous page while the resolver runs. The spinner covers that gap.
 *
 * Timing matters: it starts on GuardsCheckEnd, not NavigationStart. A canDeactivate guard can block indefinitely
 * while it waits for the user to answer a confirmation dialog (e.g. the "Leave Page?" prompt behind Quit Order).
 * The spinner renders as a full-screen p-blockUI, so starting it before guards resolve would cover that dialog and
 * swallow every click, deadlocking the page.
 *
 * Registered once at app bootstrap, so it also covers navigations that don't go through OrderBaseComponent.navigate
 * (menu clicks, redirects, deep links).
 */
@Injectable({ providedIn: 'root' })
export class NavigationSpinnerService {
  static readonly SPINNER_ID = 'order-navigation'

  readonly #router = inject(Router)
  readonly #spinnerService = inject(SpinnerService)

  #started = false

  init(): void {
    this.#router.events.subscribe(event => {
      if (event instanceof GuardsCheckEnd && event.shouldActivate) {
        this.#start()
      } else if (
        event instanceof NavigationEnd ||
        event instanceof NavigationCancel ||
        event instanceof NavigationError
      ) {
        this.#stop()
      }
    })
  }

  #start(): void {
    if (this.#started) {
      return
    }
    this.#started = true
    this.#spinnerService.setLoading(true, NavigationSpinnerService.SPINNER_ID)
  }

  #stop(): void {
    if (!this.#started) {
      return
    }
    this.#started = false
    this.#spinnerService.setLoading(false, NavigationSpinnerService.SPINNER_ID)
  }
}
